import { Heart, BookOpen, Users, Star } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  logoUrl: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  website: string | null;
}

interface AboutPageProps {
  org: Organization | undefined;
}

export function AboutPage({ org }: AboutPageProps) {
  return (
    <div className="py-12">
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-900">About {org?.name || 'Our Synagogue'}</h1>
            <p className="mt-6 text-lg text-gray-600">
              We are a welcoming Jewish community dedicated to spiritual growth, learning, and building
              meaningful connections. Our doors are open to all who seek to explore and celebrate Jewish life.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Heart className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Chesed</h3>
              <p className="text-gray-600 mt-2">
                Loving-kindness guides everything we do, from welcoming newcomers to supporting those in need.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <BookOpen className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Torah</h3>
              <p className="text-gray-600 mt-2">
                We are committed to Jewish learning for all ages, from beginners to scholars.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Kehillah</h3>
              <p className="text-gray-600 mt-2">
                Community is at our heart. We create spaces for connection, celebration, and support.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Star className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Tikkun Olam</h3>
              <p className="text-gray-600 mt-2">
                Repairing the world through social justice, volunteering, and environmental stewardship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">What We Offer</h2>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Shabbat & Holiday Services</h3>
              <p className="text-gray-600">
                Join us for meaningful prayer experiences that blend tradition with accessibility.
                All are welcome regardless of background or experience.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Religious School</h3>
              <p className="text-gray-600">
                Our engaging curriculum helps children develop a strong Jewish identity through learning,
                celebration, and community involvement.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Adult Education</h3>
              <p className="text-gray-600">
                From Hebrew reading to Talmud study, we offer classes for learners at every level.
                Explore Jewish texts, history, and contemporary issues.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lifecycle Events</h3>
              <p className="text-gray-600">
                We support our members through life's milestones: baby namings, B'nei Mitzvah,
                weddings, conversions, and memorial services.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Social & Cultural Events</h3>
              <p className="text-gray-600">
                Build friendships through Shabbat dinners, holiday celebrations, social action projects,
                and community gatherings.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Pastoral Care</h3>
              <p className="text-gray-600">
                Our clergy and caring community provide support during difficult times, including
                hospital visits, counseling, and bereavement support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-primary-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl font-bold">Become a Member</h2>
            <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto">
              Membership supports our programs and connects you to a caring community.
              We offer various membership levels to fit every family's needs.
            </p>
            <div className="mt-8">
              <a
                href="/portal#register"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-50 transition-colors"
              >
                Join Today
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
